import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const SECTIONS = [
  {
    id: 'overview',
    title: '1. Who We Are',
    body: [
      'Aloha Amino Research Inc. ("Aloha Amino", "we", "us") is a company. This Privacy Policy explains how we collect, use, disclose, and protect personal information when you use our website (alohaamino.example.com) or purchase our products.',
      'Aloha Amino is subject to applicable data protection and privacy legislation.',
    ],
  },
  {
    id: 'what-we-collect',
    title: '2. Personal Information We Collect',
    body: [
      'Account information: name, email address, institution name, and role when you register.',
      'Order information: shipping address, order details, and payment method metadata (we do not store full card numbers — payment is handled by Stripe, PCI-DSS Level 1 certified).',
      'Technical data: IP address, browser type, device identifier, pages visited, and session duration, collected via server logs and first-party analytics.',
      'Communications: any emails, support tickets, or messages you send us.',
    ],
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Information',
    body: [
      'To fulfil orders and send shipping notifications.',
      'To verify your research eligibility and maintain a compliant customer record.',
      'To send transactional emails (order confirmations, password resets, COA responses).',
      'To improve site functionality and detect fraud.',
      'To comply with legal obligations, including applicable export and research-chemical regulations.',
      'We do not use your information for advertising to third parties or sell it to data brokers.',
    ],
  },
  {
    id: 'sharing',
    title: '4. Sharing & Disclosure',
    body: [
      'Service providers: Stripe (payment processing), shipping carriers (name and address for delivery), and our email provider (transactional notifications). All processors are contractually bound to use your data only for the services they provide to us.',
      'Legal requirements: We may disclose information to law enforcement or regulators where required by applicable law, a valid court order, or to protect the rights and safety of Aloha Amino and its customers.',
      'Business transfers: In the event of a merger or acquisition, personal data may be transferred subject to the same privacy protections described here.',
      'We do not sell, rent, or trade your personal information with third parties for marketing purposes.',
    ],
  },
  {
    id: 'cookies',
    title: '5. Cookies & Tracking',
    body: [
      'We use strictly necessary cookies to maintain your session and shopping cart. We do not use third-party advertising cookies.',
      'We use first-party analytics (server-side) to understand aggregate traffic patterns. No fingerprinting or cross-site tracking is performed.',
      'You can disable cookies in your browser settings, but this may prevent the site from functioning correctly.',
    ],
  },
  {
    id: 'retention',
    title: '6. Data Retention',
    body: [
      'Account and order data is retained for a minimum of 7 years as required by applicable tax and research-compliance regulations.',
      'If you request account deletion, we will anonymise your personal identifiers within 30 days, retaining only aggregate transactional records required for compliance. Requests may be submitted to privacy@alohaamino.example.com.',
    ],
  },
  {
    id: 'rights',
    title: '7. Your Rights',
    body: [
      'You have the right to: (a) access and know what personal information we hold about you; (b) request corrections to inaccurate information; (c) request deletion of your personal information; (d) withdraw consent where processing is consent-based; and (e) lodge a complaint with the relevant data protection authority.',
      'To exercise these rights, email privacy@alohaamino.example.com. We will respond within 30 days.',
    ],
  },
  {
    id: 'hipaa',
    title: '8. HIPAA & Health Information',
    body: [
      'Aloha Amino sells products for laboratory and in-vitro research use only. We are not a healthcare provider, health plan, or healthcare clearinghouse, and we are therefore not a "covered entity" or "business associate" as those terms are defined under the U.S. Health Insurance Portability and Accountability Act (HIPAA). We do not provide medical care and do not knowingly collect protected health information (PHI), diagnoses, treatment records, or insurance information.',
      'Please do not send us medical history, health conditions, prescriptions, or any other protected health information. Our forms and accounts are not designed to receive PHI, and you should not include it in support tickets, chat messages, or order notes.',
      'Where any health-related information is nonetheless shared with us, we apply HIPAA-aligned safeguards as a matter of best practice: access is limited to authorized personnel on a need-to-know basis, data is encrypted in transit (TLS) and at rest, and we maintain administrative, physical, and technical controls designed to protect its confidentiality. We will not use or disclose such information for marketing and will delete it on request where we are not legally required to retain it.',
      'If you believe you have inadvertently sent us health information and would like it removed, email privacy@alohaamino.example.com and we will delete it promptly.',
    ],
  },
  {
    id: 'security',
    title: '9. Security',
    body: [
      'We use TLS encryption for all data in transit. Passwords are hashed using Argon2. Database backups are encrypted at rest. Payment data is handled entirely by Stripe and never stored on our servers.',
      'Despite our safeguards, no system is 100% secure. If you suspect unauthorised access, contact support@alohaamino.example.com immediately.',
    ],
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    body: [
      'We may update this Privacy Policy periodically. Material changes will be notified by email and/or a site notice at least 14 days before they take effect. The effective date at the top of this page reflects the most recent version.',
      'Last updated: June 2026.',
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <section className="bg-tea-700 text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Legal</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">Privacy Policy</h1>
          <p className="mt-3 text-tea-200">Last updated June 2026 · Aloha Amino Research Inc.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 flex gap-12">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-24">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
            <nav className="flex flex-col gap-1">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-sm text-gray-500 hover:text-tea-700 transition-colors py-1 leading-tight"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="flex flex-col gap-10">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{s.title}</h2>
                {s.body.map((para, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed mb-3">{para}</p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-4">
              Privacy questions? Email <a href="mailto:privacy@alohaamino.example.com" className="text-tea-700 underline">privacy@alohaamino.example.com</a>
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/terms" className="text-tea-700 font-medium hover:underline text-sm">Terms of Service</Link>
              <Link to="/compliance" className="text-tea-700 font-medium hover:underline text-sm">Research Use Disclosure</Link>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
